import { TsDOM as q } from '@/scripts/core/TsDOM'
import { ModalDialog } from '@/scripts/theme/modal-dialog'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import {
	ATTRIBUTES,
	isCartErrorEvent,
	isCartUpdateEvent,
	openWaitlistModal,
	PUB_SUB_EVENTS,
	subscribe,
} from '@/scripts/core/global'
import {
	FindationClient,
	FindationRequestError,
	type FindationBrand,
	type FindationProduct,
	type FindationSearchResult,
	type FindationShade,
} from '@/scripts/optional/shade-finder-api'

type ShadeFinderImage = {
	url: string
	width: number
	height: number
	alt: string
}

type ShadeFinderVariant = {
	id: string
	available: boolean
	inventoryStatus: 'InStock' | 'PreOrder' | 'ComingSoon' | 'OutOfStock'
	inventoryStatusText: string
	title: string
	price: string
	color: string
	description: string
	modelImage: ShadeFinderImage | null
	productImage: ShadeFinderImage | null
}

type ShadeFinderProductData = {
	title: string
	subtitle: string
	url: string
	variants: ShadeFinderVariant[]
}

type ShadeFinderStep = 1 | 2 | 3 | 4 | 'more-data' | 'result' | 'error'
type SearchableItem = FindationBrand | FindationProduct | FindationShade

const MAX_VISIBLE_RESULTS = 80
let sharedBrandsRequest: Promise<FindationBrand[]> | undefined
const sharedProductRequests = new Map<number, Promise<FindationProduct[]>>()
const sharedShadeRequests = new Map<number, Promise<FindationShade[]>>()

function getSharedBrands(api: FindationClient): Promise<FindationBrand[]> {
	sharedBrandsRequest ??= api.getBrands().catch((error: unknown) => {
		sharedBrandsRequest = undefined
		throw error
	})
	return sharedBrandsRequest
}

function getSharedProducts(
	api: FindationClient,
	brandId: number
): Promise<FindationProduct[]> {
	let request = sharedProductRequests.get(brandId)
	if (!request) {
		request = api.getProducts(brandId).catch((error: unknown) => {
			sharedProductRequests.delete(brandId)
			throw error
		})
		sharedProductRequests.set(brandId, request)
	}
	return request
}

function getSharedShades(
	api: FindationClient,
	productId: number
): Promise<FindationShade[]> {
	let request = sharedShadeRequests.get(productId)
	if (!request) {
		request = api.getShades(productId).catch((error: unknown) => {
			sharedShadeRequests.delete(productId)
			throw error
		})
		sharedShadeRequests.set(productId, request)
	}
	return request
}

function isProductData(value: unknown): value is ShadeFinderProductData {
	if (!value || typeof value !== 'object') return false
	const candidate = value as Partial<ShadeFinderProductData>
	return (
		typeof candidate.title === 'string' &&
		typeof candidate.subtitle === 'string' &&
		typeof candidate.url === 'string' &&
		Array.isArray(candidate.variants)
	)
}

export class ShadeFinderOpener extends UcoastEl {
	static htmlSelector = 'shade-finder-opener'

	private button: HTMLButtonElement | null

	constructor() {
		super()
		this.button = this.querySelector<HTMLButtonElement>('button')
		this.button?.addEventListener('click', this.open)
	}

	override disconnectedCallback(): void {
		this.button?.removeEventListener('click', this.open)
		super.disconnectedCallback()
	}

	private open = (event: MouseEvent): void => {
		event.preventDefault()
		if (!this.button) return
		const target = this.dataset.target
		if (!target) return
		q.os<ShadeFinder>(`#${CSS.escape(target)}`)?.show(this.button)
	}
}

export class ShadeFinder extends ModalDialog {
	static override htmlSelector = 'shade-finder'

	private readonly api: FindationClient
	private readonly productData: ShadeFinderProductData
	private readonly panels: NodeListOf<HTMLElement>
	private readonly backButton: HTMLButtonElement
	private readonly list: HTMLElement
	private readonly searchInput: HTMLInputElement
	private readonly status: HTMLElement
	private readonly errorMessage: HTMLElement
	private readonly addButton: HTMLButtonElement
	private readonly addButtonLabel: HTMLElement
	private readonly matchButton: HTMLButtonElement
	private readonly resultPanel: HTMLElement
	private readonly resultImages: HTMLElement
	private readonly resultImage: HTMLImageElement
	private readonly resultSwatch: HTMLElement
	private readonly resultTitle: HTMLElement
	private readonly resultDescription: HTMLElement
	private readonly productCard: HTMLElement
	private readonly productImage: HTMLImageElement
	private readonly productTitle: HTMLElement
	private readonly productSubtitle: HTMLElement
	private readonly productPrice: HTMLElement
	private readonly productShade: HTMLElement
	private readonly productInventoryStatus: HTMLElement
	private readonly summaryBrand: HTMLElement
	private readonly summaryProduct: HTMLElement
	private readonly summaryShade: HTMLElement
	private readonly allShades: HTMLElement
	private readonly allShadesList: HTMLElement

	private requestController?: AbortController
	private currentStep: ShadeFinderStep = 1
	private brands?: FindationBrand[]
	private items: SearchableItem[] = []
	private selectedBrand?: FindationBrand
	private selectedProduct?: FindationProduct
	private selectedShade?: FindationShade
	private matchedVariant?: ShadeFinderVariant
	private referenceShades: FindationShade[] = []
	private inertedElements: HTMLElement[] = []
	private formSubmitted = false
	private pendingCartCleanup?: () => void
	private pendingWaitlist?: {
		variantId: number
		productTitle: string
		opener: HTMLElement
	}
	private prefetchTimer?: number

	constructor() {
		super()
		const productDataSource = q.rs<HTMLScriptElement>(
			'[data-shade-finder-product]',
			this
		)
		const parsedProductData: unknown = JSON.parse(
			productDataSource.textContent ?? 'null'
		)
		if (!isProductData(parsedProductData)) {
			throw new Error('Shade Finder product data is malformed')
		}
		this.productData = parsedProductData
		this.api = new FindationClient(q.ra(this, 'data-api-key'))
		this.panels = q.rl<HTMLElement>('[data-shade-finder-panel]', this)
		this.backButton = q.rs<HTMLButtonElement>('[data-shade-finder-back]', this)
		this.list = q.rs<HTMLElement>('[data-shade-finder-list]', this)
		this.searchInput = q.rs<HTMLInputElement>('[data-shade-finder-search]', this)
		this.status = q.rs<HTMLElement>('[data-shade-finder-status]', this)
		this.errorMessage = q.rs<HTMLElement>('[data-shade-finder-error-message]', this)
		this.addButton = q.rs<HTMLButtonElement>('[data-shade-finder-add]', this)
		this.addButtonLabel = q.rs<HTMLElement>('[data-shade-finder-add-label]', this)
		this.matchButton = q.rs<HTMLButtonElement>('[data-shade-finder-match]', this)
		this.resultPanel = q.rs<HTMLElement>(
			'[data-shade-finder-panel="result"]',
			this
		)
		this.resultImages = q.rs<HTMLElement>('[data-shade-finder-result-images]', this)
		this.resultImage = q.rs<HTMLImageElement>('[data-shade-finder-result-image]', this)
		this.resultSwatch = q.rs<HTMLElement>('[data-shade-finder-result-swatch]', this)
		this.resultTitle = q.rs<HTMLElement>('[data-shade-finder-result-title]', this)
		this.resultDescription = q.rs<HTMLElement>(
			'[data-shade-finder-result-description]',
			this
		)
		this.productCard = q.rs<HTMLElement>('[data-shade-finder-product-card]', this)
		this.productImage = q.rs<HTMLImageElement>('[data-shade-finder-product-image]', this)
		this.productTitle = q.rs<HTMLElement>('[data-shade-finder-product-title]', this)
		this.productSubtitle = q.rs<HTMLElement>('[data-shade-finder-product-subtitle]', this)
		this.productPrice = q.rs<HTMLElement>('[data-shade-finder-product-price]', this)
		this.productShade = q.rs<HTMLElement>('[data-shade-finder-product-shade]', this)
		this.productInventoryStatus = q.rs<HTMLElement>(
			'[data-shade-finder-product-inventory-status]',
			this
		)
		this.summaryBrand = q.rs<HTMLElement>('[data-shade-finder-summary-brand]', this)
		this.summaryProduct = q.rs<HTMLElement>('[data-shade-finder-summary-product]', this)
		this.summaryShade = q.rs<HTMLElement>('[data-shade-finder-summary-shade]', this)
		this.allShades = q.rs<HTMLElement>('[data-shade-finder-all-shades]', this)
		this.allShadesList = q.rs<HTMLElement>('[data-shade-finder-all-shades-list]', this)

		this.backButton.addEventListener('click', this.goBack)
		this.searchInput.addEventListener('input', this.filterItems)
		q.rs<HTMLButtonElement>('[data-shade-finder-clear]', this).addEventListener('click', () => {
			this.searchInput.value = ''
			this.searchInput.dispatchEvent(new Event('input', { bubbles: true }))
			this.searchInput.focus()
		})
		this.list.addEventListener('click', this.selectItem)
		this.list.addEventListener('pointerover', this.schedulePointerPrefetch)
		this.list.addEventListener('pointerout', this.cancelPrefetch)
		this.list.addEventListener('focusin', this.prefetchFocusedItem)
		this.matchButton.addEventListener('click', this.findMatch)
		q.rs<HTMLButtonElement>('[data-shade-finder-change]', this).addEventListener(
			'click',
			() => this.showStep(3)
		)
		q.rl<HTMLButtonElement>('[data-shade-finder-restart]', this).forEach((button) => {
			button.addEventListener('click', this.restart)
		})
		q.rs<HTMLButtonElement>('[data-shade-finder-retry]', this).addEventListener(
			'click',
			this.loadBrands
		)
		q.rs<HTMLButtonElement>('[data-shade-finder-add-reference]', this).addEventListener(
			'click',
			this.addAnotherReference
		)
		q.rs<HTMLButtonElement>('[data-shade-finder-shop-all]', this).addEventListener(
			'click',
			this.showAllShadesFallback
		)
		this.addButton.addEventListener('click', this.addMatchedVariant)
		this.allShadesList.addEventListener('click', this.selectAlternativeVariant)
	}

	override show(opener: HTMLElement): void {
		this.formSubmitted = false
		this.restart()
		super.show(opener)
		this.inertBackground()
		void this.loadBrands()
	}

	override hide(preventFocus = false): void {
		this.requestController?.abort()
		this.clearPendingCartRequest()
		this.restoreBackground()
		super.hide(preventFocus)
	}

	override disconnectedCallback(): void {
		this.requestController?.abort()
		this.cancelPrefetch()
		this.list.removeEventListener('pointerover', this.schedulePointerPrefetch)
		this.list.removeEventListener('pointerout', this.cancelPrefetch)
		this.list.removeEventListener('focusin', this.prefetchFocusedItem)
		this.clearPendingCartRequest()
		this.restoreBackground()
		super.disconnectedCallback()
	}

	protected override onHidden(): void {
		this.requestController?.abort()
		this.restoreBackground()
		if (this.pendingWaitlist) {
			const pendingWaitlist = this.pendingWaitlist
			this.pendingWaitlist = undefined
			this.restart()
			openWaitlistModal(
				pendingWaitlist.variantId,
				pendingWaitlist.productTitle,
				pendingWaitlist.opener
			)
			return
		}
		if (!this.formSubmitted) this.restart()
	}

	private restart = (): void => {
		this.requestController?.abort()
		this.selectedBrand = undefined
		this.selectedProduct = undefined
		this.selectedShade = undefined
		this.matchedVariant = undefined
		this.referenceShades = []
		this.searchInput.value = ''
		this.showStep(1)
		if (this.brands) {
			this.items = this.brands
			this.renderItems()
		}
	}

	private loadBrands = async (): Promise<void> => {
		this.showStep(1)
		if (this.brands) {
			this.items = this.brands
			this.renderItems()
			this.focusSearch()
			return
		}

		const controller = this.beginRequest()
		this.setBusy(true, this.dataset.loading ?? '')
		try {
			const [available, brands] = await Promise.all([
				this.api.isAvailable(this.productData.url, controller.signal),
				getSharedBrands(this.api),
			])
			if (!available) {
				this.showError(this.dataset.unavailable ?? '')
				return
			}
			this.brands = brands
			this.items = this.brands
			this.searchInput.value = ''
			this.renderItems()
			this.focusSearch()
		} catch (error) {
			this.handleRequestError(error)
		} finally {
			if (this.requestController === controller) {
				this.requestController = undefined
				this.setBusy(false)
			}
		}
	}

	private loadProducts = async (brand: FindationBrand): Promise<void> => {
		this.selectedBrand = brand
		this.selectedProduct = undefined
		this.selectedShade = undefined
		this.showStep(2)
		const controller = this.beginRequest()
		this.setBusy(true, this.dataset.loading ?? '')
		try {
			const products = await getSharedProducts(this.api, brand.id)
			if (controller.signal.aborted) return
			this.items = products
			this.searchInput.value = ''
			this.renderItems()
			this.focusSearch()
		} catch (error) {
			this.handleRequestError(error)
		} finally {
			if (this.requestController === controller) {
				this.requestController = undefined
				this.setBusy(false)
			}
		}
	}

	private loadShades = async (product: FindationProduct): Promise<void> => {
		this.selectedProduct = product
		this.selectedShade = undefined
		this.showStep(3)
		const controller = this.beginRequest()
		this.setBusy(true, this.dataset.loading ?? '')
		try {
			const shades = await getSharedShades(this.api, product.id)
			if (controller.signal.aborted) return
			this.items = shades
			this.searchInput.value = ''
			this.renderItems()
			this.focusSearch()
		} catch (error) {
			this.handleRequestError(error)
		} finally {
			if (this.requestController === controller) {
				this.requestController = undefined
				this.setBusy(false)
			}
		}
	}

	private findMatch = async (): Promise<void> => {
		const selectedShade = this.selectedShade
		if (!selectedShade) return
		const references = [
			...this.referenceShades.filter(
				(reference) => reference.id !== selectedShade.id
			),
			selectedShade,
		]
		const controller = this.beginRequest()
		this.setBusy(true, this.dataset.matching ?? '')
		try {
			const result = await this.api.search(
				references.map((reference) => reference.id),
				this.productData.url,
				controller.signal
			)
			this.handleMatch(result, references)
		} catch (error) {
			this.handleRequestError(error)
		} finally {
			if (this.requestController === controller) {
				this.requestController = undefined
				this.setBusy(false)
			}
		}
	}

	private handleMatch(
		result: FindationSearchResult,
		references: FindationShade[]
	): void {
		if (result.kind === 'more-data') {
			this.referenceShades = references
			this.showStep('more-data')
			if (this.hasAttribute('data-uc-modal-open')) {
				q.os<HTMLButtonElement>('[data-shade-finder-add-reference]', this)?.focus()
			}
			return
		}

		const variant = this.productData.variants.find(
			(candidate) => candidate.id === result.externalId
		)
		if (!variant) {
			this.showAllShadesFallback()
			return
		}

		this.renderResult(variant)
		this.renderShadeChoices(!this.isPurchasable(variant) && !this.requiresWaitlist(variant))
		this.showStep('result')
		if (this.hasAttribute('data-uc-modal-open')) {
			if (this.isPurchasable(variant) || this.requiresWaitlist(variant)) {
				this.addButton.focus()
			} else {
				this.allShadesList.querySelector<HTMLButtonElement>('button')?.focus()
			}
		}
	}

	private renderResult(variant: ShadeFinderVariant): void {
		this.resultPanel.dataset.shadeFinderResultMode = 'matched'
		this.matchedVariant = variant
		this.resultImages.hidden = false
		this.resultTitle.textContent = variant.title
		this.resultDescription.textContent = variant.description
		this.resultDescription.hidden = variant.description === ''
		this.resultSwatch.style.backgroundColor = variant.color
		this.resultSwatch.hidden = variant.color === ''
		this.setImage(this.resultImage, variant.modelImage)
		this.setImage(this.productImage, variant.productImage)
		this.productCard.hidden = false
		this.productTitle.textContent = this.productData.title
		this.productSubtitle.textContent = this.productData.subtitle
		this.productPrice.textContent = variant.price
		this.productShade.textContent = variant.title
		this.productInventoryStatus.textContent = variant.inventoryStatusText
		this.productInventoryStatus.hidden = variant.inventoryStatus === 'InStock'
		this.updateAddButton(variant)
	}

	private renderAllShadesFallback(): void {
		this.resultPanel.dataset.shadeFinderResultMode = 'manual'
		this.matchedVariant = undefined
		this.resultImages.hidden = true
		this.resultTitle.textContent = this.dataset.noMatchHeading ?? ''
		this.resultDescription.textContent = this.dataset.noMatchBody ?? ''
		this.resultDescription.hidden = false
		this.productCard.hidden = true
		this.addButton.disabled = true
		this.addButtonLabel.textContent = this.dataset.selectShade ?? ''
		this.renderShadeChoices(true)
	}

	private showAllShadesFallback = (): void => {
		this.renderAllShadesFallback()
		this.showStep('result')
		if (this.hasAttribute('data-uc-modal-open')) {
			this.allShadesList.querySelector<HTMLButtonElement>('button')?.focus()
		}
	}

	private renderShadeChoices(show: boolean): void {
		this.allShades.hidden = !show
		if (!show) {
			this.allShadesList.replaceChildren()
			return
		}
		const fragment = document.createDocumentFragment()
		this.productData.variants.forEach((variant) => {
			const item = document.createElement('li')
			const button = document.createElement('button')
			const swatch = document.createElement('span')
			const label = document.createElement('span')
			const shadeNumber = document.createElement('span')
			const shadeName = document.createElement('span')
			button.type = 'button'
			button.dataset.shadeFinderVariantId = variant.id
			button.setAttribute('aria-pressed', `${variant.id === this.matchedVariant?.id}`)
			const purchasable = this.isPurchasable(variant)
			button.toggleAttribute('data-unavailable', !purchasable)
			button.setAttribute(
				'aria-label',
				variant.inventoryStatus === 'InStock'
					? variant.title
					: `${variant.title}, ${variant.inventoryStatusText}`
			)
			swatch.className = 'shade-finder__variant-swatch'
			swatch.style.backgroundColor = variant.color
			label.className = 'shade-finder__variant-label'
			const [number, ...name] = variant.title.trim().split(/\s+/)
			shadeNumber.textContent = number
			shadeName.textContent = name.join(' ')
			label.append(shadeNumber, shadeName)
			button.append(swatch, label)
			item.appendChild(button)
			fragment.appendChild(item)
		})
		this.allShadesList.replaceChildren(fragment)
	}

	private selectAlternativeVariant = (event: MouseEvent): void => {
		const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
			'[data-shade-finder-variant-id]'
		)
		if (!button) return
		const variant = this.productData.variants.find(
			(candidate) => candidate.id === button.dataset.shadeFinderVariantId
		)
		if (!variant) return
		if (this.resultPanel.dataset.shadeFinderResultMode === 'manual') {
			this.matchedVariant = variant
			this.updateAddButton(variant)
			q.rl<HTMLButtonElement>(
				'[data-shade-finder-variant-id]',
				this.allShadesList
			).forEach((choice) => {
				choice.setAttribute(
					'aria-pressed',
					`${choice.dataset.shadeFinderVariantId === variant.id}`
				)
			})
			return
		}
		this.renderResult(variant)
		this.renderShadeChoices(!this.isPurchasable(variant) && !this.requiresWaitlist(variant))
	}

	private updateAddButton(variant: ShadeFinderVariant): void {
		const waitlist = this.requiresWaitlist(variant)
		this.addButton.disabled = !this.isPurchasable(variant) && !waitlist
		this.addButtonLabel.textContent = waitlist
			? this.dataset.notifyMe ?? ''
			: variant.inventoryStatus === 'PreOrder'
				? variant.inventoryStatusText
				: this.dataset.addToBag ?? ''
	}

	private isPurchasable(variant: ShadeFinderVariant): boolean {
		return (
			variant.available &&
			(variant.inventoryStatus === 'InStock' || variant.inventoryStatus === 'PreOrder')
		)
	}

	private requiresWaitlist(variant: ShadeFinderVariant): boolean {
		return (
			variant.inventoryStatus === 'ComingSoon' ||
			variant.inventoryStatus === 'OutOfStock'
		)
	}

	private addAnotherReference = (): void => {
		this.selectedBrand = undefined
		this.selectedProduct = undefined
		this.selectedShade = undefined
		this.searchInput.value = ''
		if (!this.brands) {
			void this.loadBrands()
			return
		}
		this.items = this.brands
		this.showStep(1)
		this.renderItems()
		this.focusSearch()
	}

	private addMatchedVariant = (): void => {
		if (!this.matchedVariant) return
		if (this.requiresWaitlist(this.matchedVariant)) {
			this.pendingWaitlist = {
				variantId: Number(this.matchedVariant.id),
				productTitle: `${this.productData.title} — ${this.matchedVariant.title}`,
				opener: this.openedBy ?? this.addButton,
			}
			this.hide(true)
			return
		}
		const form = document.getElementById(this.dataset.productFormId ?? '')
		if (!(form instanceof HTMLFormElement)) {
			this.showError(this.dataset.error ?? '')
			return
		}
		const standaloneVariantInput = form.querySelector<HTMLInputElement>(
			'[data-shade-finder-variant-input]'
		)
		const product = form.closest<HTMLElement>('hero-featured-product')
		const variantInput = product?.querySelector<HTMLInputElement>(
			`input[type="radio"][value="${CSS.escape(this.matchedVariant.id)}"]`
		)
		const submitButton = form.querySelector<HTMLButtonElement>('[name="add"]')
		if (
			(!standaloneVariantInput && !variantInput) ||
			!submitButton ||
			!this.isPurchasable(this.matchedVariant)
		) {
			this.showError(this.dataset.unknownVariant ?? '')
			return
		}

		if (standaloneVariantInput) {
			standaloneVariantInput.value = this.matchedVariant.id
		} else if (variantInput) {
			variantInput.checked = true
			variantInput.dispatchEvent(new Event('change', { bubbles: true }))
		}
		this.setAddButtonLoading(true)
		const matchedVariantId = this.matchedVariant.id
		const unsubscribeUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
			if (
				!isCartUpdateEvent(event) ||
				event.source !== 'product-form' ||
				`${event.productVariantId}` !== matchedVariantId
			)
				return
			this.formSubmitted = true
			this.clearPendingCartRequest()
			this.hide(true)
		})
		const unsubscribeError = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
			if (
				!isCartErrorEvent(event) ||
				event.source !== 'product-form' ||
				`${event.productVariantId}` !== matchedVariantId
			)
				return
			this.clearPendingCartRequest()
			this.showError(event.message || this.dataset.error || '')
		})
		this.pendingCartCleanup = () => {
			unsubscribeUpdate()
			unsubscribeError()
			this.pendingCartCleanup = undefined
			this.setAddButtonLoading(false)
		}
		form.requestSubmit(submitButton)
	}

	private clearPendingCartRequest(): void {
		this.pendingCartCleanup?.()
	}

	private setAddButtonLoading(loading: boolean): void {
		this.addButton.disabled = loading
		this.addButton.toggleAttribute(ATTRIBUTES.loading, loading)
		this.addButton
			.querySelector<HTMLElement>('[data-uc-loading-overlay-spinner]')
			?.classList.toggle('hidden', !loading)
	}

	private setImage(
		image: HTMLImageElement,
		value: ShadeFinderImage | null
	): void {
		if (!value) {
			image.hidden = true
			image.removeAttribute('src')
			return
		}
		image.src = value.url
		image.width = value.width
		image.height = value.height
		image.alt = value.alt
		image.hidden = false
	}

	private showStep(step: ShadeFinderStep): void {
		this.currentStep = step
		this.panels.forEach((panel) => {
			const panelName = panel.dataset.shadeFinderPanel
			panel.hidden =
				panelName === 'search'
					? typeof step !== 'number' || step > 3
					: panelName !== `${step}`
		})
		this.backButton.hidden =
			step === 1 || step === 'more-data' || step === 'result' || step === 'error'
		if (step === 1) this.searchInput.placeholder = this.dataset.searchBrands ?? ''
		else if (step === 2)
			this.searchInput.placeholder = this.dataset.searchProducts ?? ''
		else if (step === 3)
			this.searchInput.placeholder = this.dataset.searchShades ?? ''
		if (typeof step === 'number' && step < 4) {
			this.list.replaceChildren()
		}
		if (step === 4) {
			this.summaryBrand.textContent = this.selectedBrand?.name ?? ''
			this.summaryProduct.textContent = this.selectedProduct?.name ?? ''
			this.summaryShade.textContent = this.selectedShade?.name ?? ''
			window.requestAnimationFrame(() => {
				if (this.hasAttribute('data-uc-modal-open')) this.matchButton.focus()
			})
		}
		const activePanel = Array.from(this.panels).find(
			(panel) => panel.dataset.shadeFinderPanel === `${step}`
		)
		this.status.textContent = activePanel?.querySelector('h2')?.textContent ?? ''
	}

	private goBack = (): void => {
		if (typeof this.currentStep !== 'number' || this.currentStep <= 1) return
		if (this.currentStep === 2) {
			this.items = this.brands ?? []
			this.showStep(1)
		} else if (this.currentStep === 3 && this.selectedBrand) {
			void this.loadProducts(this.selectedBrand)
			return
		} else if (this.currentStep === 4 && this.selectedProduct) {
			void this.loadShades(this.selectedProduct)
			return
		}
		this.searchInput.value = ''
		this.renderItems()
		this.focusSearch()
	}

	private selectItem = (event: MouseEvent): void => {
		const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
			'[data-shade-finder-item-id]'
		)
		if (!button) return
		const id = Number(button.dataset.shadeFinderItemId)
		const item = this.items.find((candidate) => candidate.id === id)
		if (!item) return

		if (this.currentStep === 1) {
			void this.loadProducts(item as FindationBrand)
		} else if (this.currentStep === 2) {
			void this.loadShades(item as FindationProduct)
		} else if (this.currentStep === 3) {
			this.selectedShade = item as FindationShade
			this.showStep(4)
		}
	}

	private filterItems = (): void => this.renderItems()

	private schedulePointerPrefetch = (event: PointerEvent): void => {
		this.schedulePrefetch(event.target, 180)
	}

	private prefetchFocusedItem = (event: FocusEvent): void => {
		this.schedulePrefetch(event.target, 0)
	}

	private schedulePrefetch(target: EventTarget | null, delay: number): void {
		this.cancelPrefetch()
		if (!(target instanceof Element)) return
		const button = target.closest<HTMLButtonElement>(
			'[data-shade-finder-item-id]'
		)
		if (!button) return
		const id = Number(button.dataset.shadeFinderItemId)
		const item = this.items.find((candidate) => candidate.id === id)
		if (!item || (this.currentStep !== 1 && this.currentStep !== 2)) return
		this.prefetchTimer = window.setTimeout(() => {
			this.prefetchTimer = undefined
			if (this.currentStep === 1) {
				void getSharedProducts(this.api, item.id).catch(() => undefined)
			} else if (this.currentStep === 2) {
				void getSharedShades(this.api, item.id).catch(() => undefined)
			}
		}, delay)
	}

	private cancelPrefetch = (): void => {
		if (this.prefetchTimer === undefined) return
		window.clearTimeout(this.prefetchTimer)
		this.prefetchTimer = undefined
	}

	private renderItems(): void {
		q.rs<HTMLButtonElement>('[data-shade-finder-clear]', this).hidden = this.searchInput.value.length === 0
		const query = this.searchInput.value.trim().toLocaleLowerCase()
		const matches = this.items
			.filter((item) => `${item.name} ${item.search}`.toLocaleLowerCase().includes(query))
			.slice(0, MAX_VISIBLE_RESULTS)
		const fragment = document.createDocumentFragment()
		matches.forEach((item) => {
			const listItem = document.createElement('li')
			const button = document.createElement('button')
			button.type = 'button'
			button.dataset.shadeFinderItemId = `${item.id}`
			button.textContent = item.name
			if (
				this.currentStep === 3 &&
				this.referenceShades.some((reference) => reference.id === item.id)
			) {
				button.disabled = true
			}
			listItem.appendChild(button)
			fragment.appendChild(listItem)
		})
		this.list.replaceChildren(fragment)
		const countTemplate =
			matches.length === 1
				? this.dataset.resultsCountOne
				: this.dataset.resultsCountOther
		this.status.textContent = matches.length
			? (countTemplate ?? '').replace('__COUNT__', `${matches.length}`)
			: this.dataset.empty ?? ''
	}

	private showError(message: string): void {
		this.errorMessage.textContent = message
		this.showStep('error')
		this.status.textContent = message
		q.os<HTMLButtonElement>('[data-shade-finder-retry]', this)?.focus()
	}

	private handleRequestError(error: unknown): void {
		if (error instanceof FindationRequestError && error.code === 'aborted') return
		this.showError(this.dataset.error ?? '')
	}

	private beginRequest(): AbortController {
		this.requestController?.abort()
		const controller = new AbortController()
		this.requestController = controller
		return controller
	}

	private setBusy(busy: boolean, message = ''): void {
		if (busy) this.setAttribute('aria-busy', 'true')
		else this.removeAttribute('aria-busy')
		this.searchInput.disabled = busy
		if (busy) {
			this.items = []
			this.status.textContent = message
			this.list.replaceChildren()
		}
	}

	private focusSearch(): void {
		window.requestAnimationFrame(() => {
			if (this.hasAttribute('data-uc-modal-open')) this.searchInput.focus()
		})
	}

	private inertBackground(): void {
		this.restoreBackground()
		this.inertedElements = Array.from(document.body.children).flatMap((element) => {
			if (!(element instanceof HTMLElement) || element === this || element.inert) return []
			element.inert = true
			return [element]
		})
	}

	private restoreBackground(): void {
		this.inertedElements.forEach((element) => {
			element.inert = false
		})
		this.inertedElements = []
	}
}
